import { Link } from 'react-router-dom';
import { BadgeCheck } from 'lucide-react';
import { formatPhone, usePublicSettings } from '@/layouts/PublicShell';
import { Clause, PageHero, Prose } from './shared';
import { DraftNotice } from './Privacy';

const LAST_UPDATED = '8 August 2026';

/**
 * Terms and conditions.
 *
 * Every clause here describes behaviour the software actually enforces. The
 * money clauses in particular are not aspirational: there is no interest, fee
 * or penalty column anywhere in the schema, so clause 4 could not be breached
 * by a change of mind — only by rebuilding the system.
 */
export default function Terms() {
  const org = usePublicSettings();

  return (
    <>
      <PageHero
        eyebrow="Terms and conditions"
        title="The terms of an AAF-UNIFEMGA loan."
        description="What you agree to when you borrow, and what the association commits to in return."
      >
        <p className="numeric mt-6 text-sm text-foreground-secondary">
          Last updated {LAST_UPDATED} · Version 1.0
        </p>
      </PageHero>

      <Prose>
        <DraftNotice />

        {/* The clause that matters most, stated before the small print. */}
        <div className="flex gap-4 rounded-2xl border-2 border-success/30 bg-success-soft/50 p-6">
          <BadgeCheck size={22} className="mt-0.5 shrink-0 text-success" />
          <div>
            <h2 className="font-heading text-lg font-bold text-foreground">
              You repay exactly what you borrow.
            </h2>
            <p className="mt-1.5 text-sm leading-relaxed text-foreground-secondary">
              No interest. No fees. No penalties. Nothing in these terms creates a charge of any
              kind, and nothing later in this document takes that back.
            </p>
          </div>
        </div>

        <Clause number="1" title="Who these terms are between">
          <p>
            The money lent under this scheme is provided by the <strong>Aliu Abdulkabir Foundation</strong>. The scheme itself is administered by the{' '}
            <strong>Obafemi Awolowo University Muslim Graduates&rsquo; Association</strong>, which
            confirms membership, reviews applications and keeps the records.
          </p>
          <p>
            These terms are between you and both bodies together, referred to here as
            AAF-UNIFEMGA. They apply from the moment you create an account and for as long as you
            hold a loan.
          </p>
        </Clause>

        <Clause number="2" title="Eligibility">
          <p>To apply for a loan you must:</p>
          <ul className="ml-5 list-disc space-y-1.5">
            <li>be a financial member of UNIFEMGA in good standing;</li>
            <li>have completed identity verification (KYC) and had it approved;</li>
            <li>hold a Nigerian bank account in your own name;</li>
            <li>provide the required number of guarantors for the product you apply for; and</li>
            <li>not have a loan currently in arrears.</li>
          </ul>
          <p>
            Meeting these conditions makes you eligible to apply. It does not entitle you to a loan;
            every application is reviewed on its merits.
          </p>
        </Clause>

        <Clause number="3" title="Your account">
          <p>
            Your account is yours alone. Keep your password private and tell us immediately if you
            think someone else has access to it. You are responsible for what is done through your
            account.
          </p>
          <p>
            The information you give us must be true and kept current. Giving false information —
            about your identity, your income, or your guarantors&rsquo; agreement — is grounds to
            decline an application or recall a loan.
          </p>
        </Clause>

        <Clause number="4" title="Interest, fees and charges">
          <p>
            <strong className="text-foreground">
              No interest is charged on any loan, at any time, in any circumstance — not by the
              Foundation and not by the association.
            </strong>{' '}
            Every loan is <em>qard hasan</em> — a benevolent loan. The total amount repayable is the
            amount disbursed to you.
          </p>
          <p>We also charge no:</p>
          <ul className="ml-5 list-disc space-y-1.5">
            <li>application, processing or administration fee;</li>
            <li>service, management or maintenance charge;</li>
            <li>late payment penalty, default fee or collection charge; or</li>
            <li>early repayment charge.</li>
          </ul>
          <p>
            This is structural rather than a matter of policy: the platform holds no field in which
            an interest amount, fee or penalty could be recorded against a loan.
          </p>
        </Clause>

        <Clause number="5" title="Applying, and the decision">
          <p>
            You choose an amount and a repayment period within the limits of the product, state what
            the money is for and how you intend to repay it, and give the details of your
            guarantors.
          </p>
          <p>
            A reviewer may approve your application in full, approve a smaller amount or a different
            period, ask you for more information, or decline it. Where an application is declined or
            reduced you will be told why.
          </p>
        </Clause>

        <Clause number="6" title="Guarantors">
          <p>
            Guarantors are the whole of the credit assessment here, because there is no interest
            rate through which risk could otherwise be priced.
          </p>
          <ul className="ml-5 list-disc space-y-1.5">
            <li>
              You must speak to each person before naming them. The application assumes you have.
            </li>
            <li>
              An officer telephones every guarantor to confirm. No application is approved until the
              required number have confirmed by telephone.
            </li>
            <li>
              A guarantor does not need an account on this platform and is never required to use it.
            </li>
            <li>
              A guarantor&rsquo;s exposure can never exceed the amount you borrowed, because nothing
              is ever added to that amount.
            </li>
          </ul>
        </Clause>

        <Clause number="7" title="Disbursement">
          <p>
            An approved loan is paid into the bank account you have nominated. We pay to that
            account only; we do not pay to third parties. Make sure the account details are correct
            — a payment sent to a wrong account you supplied is your loss to recover.
          </p>
        </Clause>

        <Clause number="8" title="Repayment">
          <p>
            Your loan is divided into equal repayments over the period approved, and the schedule is
            visible in your portal from the day the loan is disbursed. Repayments are applied to the
            oldest amount due first.
          </p>
          <p>
            You may repay early, in part or in full, at no cost. Because nothing accrues, early
            repayment simply clears the balance sooner.
          </p>
        </Clause>

        <Clause number="9" title="If you fall behind">
          <p>
            Missing a repayment does not increase what you owe. Your balance is exactly what it was.
            What happens instead is:
          </p>
          <ul className="ml-5 list-disc space-y-1.5">
            <li>we contact you, and where appropriate discuss a revised arrangement;</li>
            <li>we may contact the guarantors who stood for you;</li>
            <li>
              if the arrears continue past the threshold the association has set, the loan may be
              recorded as defaulted; and
            </li>
            <li>
              your eligibility to borrow again is suspended for a period set by the association.
            </li>
          </ul>
          <p>
            <strong className="text-foreground">
              At no point does any of this add money to your balance.
            </strong>{' '}
            The consequence of default is loss of access to further borrowing, not a larger debt.
          </p>
        </Clause>

        <Clause number="10" title="Recovering an outstanding balance">
          <p>
            Where a loan remains unpaid, the association may pursue the outstanding principal
            through your guarantors or by any lawful means. The amount recoverable is limited to the
            principal that remains outstanding.
          </p>
        </Clause>

        <Clause number="11" title="Suspending or closing an account">
          <p>
            We may suspend access where we suspect fraud, where information given to us proves
            false, or where you cease to be a member. Suspension does not cancel a loan you are
            already repaying, and it does not change what you owe.
          </p>
        </Clause>

        <Clause number="12" title="Your information">
          <p>
            How we handle your personal information is set out in our{' '}
            <Link to="/privacy" className="font-semibold text-primary hover:underline">
              privacy policy
            </Link>
            , which forms part of these terms.
          </p>
        </Clause>

        <Clause number="13" title="Changes to these terms">
          <p>
            We may update these terms. A new version is published here with a new version number,
            and members are told directly where the change is significant. A change to these terms
            can never introduce interest, a fee or a penalty — that limit is not ours to relax.
          </p>
        </Clause>

        <Clause number="14" title="Governing law and complaints">
          <p>
            These terms are governed by the laws of the Federal Republic of Nigeria. If something
            goes wrong, please raise it with us first — telephone{' '}
            <a href={`tel:${org['org.phone']}`} className="numeric font-semibold text-primary hover:underline">
              {formatPhone(org['org.phone'])}
            </a>{' '}
            or write to{' '}
            <a href={`mailto:${org['org.email']}`} className="font-semibold text-primary hover:underline">
              {org['org.email']}
            </a>
            . Most difficulties are resolved by a conversation.
          </p>
        </Clause>

        <p className="border-t border-border pt-8 text-sm text-foreground-secondary">
          See also our{' '}
          <Link to="/privacy" className="font-semibold text-primary hover:underline">
            privacy policy
          </Link>{' '}
          and the{' '}
          <Link to="/faq" className="font-semibold text-primary hover:underline">
            frequently asked questions
          </Link>
          .
        </p>
      </Prose>
    </>
  );
}
